import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { 
  User, Building, MapPin, Phone, Globe, FileText, 
  Users, Clock, CheckCircle, XCircle, Crown,
  Edit2, Save, Loader2, AlertCircle, CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = 'copper' }) {
  const colors = {
    copper: 'bg-copper/10 text-copper',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-navy">{value}</p>
          <p className="text-sm text-slate-600">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function SupplierDashboard({ PaymentMessage }) {
  const { user, token, logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [introductions, setIntroductions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [profileRes, introsRes] = await Promise.all([
        fetch(`${API}/users/me`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/introductions/interested-buyers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
        setEditForm(profileData);
      }

      if (introsRes.ok) {
        const introsData = await introsRes.json();
        setIntroductions(introsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          company: editForm.company,
          phone: editForm.phone,
          state: editForm.state,
          bio: editForm.bio,
          website: editForm.website,
          categories: editForm.categories
        })
      });

      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        setEditing(false);
        refreshUser();
      }
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API}/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });

      const data = await res.json();

      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.detail || 'Error creating checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error creating checkout session');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-copper" />
        </div>
      </Layout>
    );
  }

  const pendingIntros = introductions.filter(i => i.status === 'pending').length;
  const approvedIntros = introductions.filter(i => i.status === 'approved').length;

  return (
    <Layout>
      <div className="bg-slate-50 min-h-screen">
        <div className="container-custom py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                Supplier Dashboard
              </h1>
              <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="text-sm text-slate-600 hover:text-copper"
            >
              Sign Out
            </button>
          </div>

          <PaymentMessage />

          {/* Membership Banner */}
          {profile?.membership_status !== 'active' && (
            <div className="bg-gradient-to-r from-navy to-navy/90 rounded-2xl p-6 mb-8 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-copper flex items-center justify-center">
                    <Crown className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Upgrade to Full Membership</h3>
                    <p className="text-white/80 text-sm">Get priority placement, full buyer access, and more</p>
                  </div>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="btn-copper"
                  data-testid="upgrade-membership-btn"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      $888/year — Join Now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label="Interested Buyers" value={introductions.length} color="blue" />
            <StatCard icon={Clock} label="Pending" value={pendingIntros} color="yellow" />
            <StatCard icon={CheckCircle} label="Approved" value={approvedIntros} color="green" />
            <StatCard 
              icon={Crown} 
              label="Membership" 
              value={profile?.membership_status === 'active' ? 'Active' : 'Free'} 
              color={profile?.membership_status === 'active' ? 'green' : 'copper'} 
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Profile Section */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-semibold text-navy text-lg">Your Profile</h2>
                  {!editing ? (
                    <button
                      onClick={() => setEditing(true)}
                      className="text-copper hover:text-copper-dark flex items-center gap-1 text-sm"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  ) : (
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Name</label>
                    {editing ? (
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="input-field mt-1"
                      />
                    ) : (
                      <p className="text-navy font-medium flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {profile?.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Company</label>
                    {editing ? (
                      <input
                        type="text"
                        value={editForm.company || ''}
                        onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                        className="input-field mt-1"
                      />
                    ) : (
                      <p className="text-navy font-medium flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-400" />
                        {profile?.company || 'Not set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">State</label>
                    {editing ? (
                      <input
                        type="text"
                        value={editForm.state || ''}
                        onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                        className="input-field mt-1"
                      />
                    ) : (
                      <p className="text-navy font-medium flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {profile?.state || 'Not set'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wide">Bio</label>
                    {editing ? (
                      <textarea
                        value={editForm.bio || ''}
                        onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                        rows={3}
                        className="input-field mt-1 resize-none"
                        placeholder="Tell buyers about your products..."
                      />
                    ) : (
                      <p className="text-slate-600 text-sm mt-1">
                        {profile?.bio || 'No bio yet'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Introductions Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-semibold text-navy text-lg mb-6">Interested Buyers</h2>

                {introductions.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No buyer interest yet</p>
                    <p className="text-sm mt-1">Complete your profile to attract buyers</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {introductions.map((intro) => (
                      <div
                        key={intro.id}
                        className="border border-slate-200 rounded-xl p-4 hover:border-copper/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium text-navy">{intro.buyer_name}</h3>
                            <p className="text-sm text-slate-600">{intro.buyer_company}</p>
                            {intro.message && (
                              <p className="text-sm text-slate-500 mt-2 italic">"{intro.message}"</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            intro.status === 'approved' ? 'bg-green-100 text-green-700' :
                            intro.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            intro.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {intro.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
